import React from 'react';
import { Loader, Switch } from '@mantine/core';
import { Button } from '../../../../components/Button';
import { useWoltMenuSection } from './useWoltMenuSection';
import classes from '../../monitoring-page.module.scss';

type WoltMenuSectionProps = {
  tenantId: string | null;
  employeeSession: string | null;
};

// Lean mapping surface inside מעקב וולט: per-product include toggle + full-replace sync button.
export const WoltMenuSection: React.FC<WoltMenuSectionProps> = ({ tenantId, employeeSession }) => {
  const section = useWoltMenuSection({ tenantId, employeeSession });

  if (!section.tenant || !section.woltConnected) {
    return (
      <div className={classes.emptyState} data-testid="monitoring-wolt-menu-disconnected">
        <p>{'וולט אינו מחובר לעסק הנבחר. אפשר לשלוח את בעל העסק לחיבור עצמי מול וולט.'}</p>
        {section.tenant && (
          <Button
            variant="save"
            size="sm"
            loading={section.isLinking}
            loadingLabel="מכין קישור..."
            onClick={section.connectToWolt}
            dataTestId="monitoring-wolt-connect"
          >
            {'חבר לוולט'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={classes.sectionStack} data-testid="monitoring-wolt-menu">
      <div className={classes.woltMenuHeader}>
        <h3 className={classes.sectionTitle}>{'תפריט וולט'}</h3>
        <p className={classes.subtitle} data-testid="monitoring-wolt-venue-id">
          {`מזהה סניף וולט: ${section.tenant.woltVenueId ?? '—'}`}
        </p>
        <Button
          variant="save"
          size="sm"
          loading={section.isSyncing}
          loadingLabel="מסנכרן..."
          onClick={section.syncMenu}
          dataTestId="monitoring-wolt-menu-sync"
        >
          {'סנכרן תפריט'}
        </Button>
      </div>
      {!section.tenant.woltVenueId && (
        <div className={classes.emptyState} data-testid="monitoring-wolt-no-venue">
          {'אין מזהה סניף וולט לעסק זה — וולט לא תוכל לנתב אליו webhooks. יש להזין מזהה סניף במסך ניהול חברות.'}
        </div>
      )}
      {!section.canEdit ? (
        <div className={classes.emptyState} data-testid="monitoring-wolt-menu-no-session">
          {'לעריכת המוצרים בתפריט יש להתחבר כמנהל לעסק זה במסך הקופה.'}
        </div>
      ) : section.isLoading && section.products.length === 0 ? (
        <div className={classes.loadingWrap}>
          <Loader />
        </div>
      ) : (
        <div className={classes.tableWrap}>
          {section.error && <p className={classes.error}>{section.error}</p>}
          <table className={classes.table} data-testid="monitoring-wolt-menu-table">
            <thead>
              <tr>
                <th>{'מוצר'}</th>
                <th>{'מחיר'}</th>
                <th>{'מזהה בוולט'}</th>
                <th>{'כלול בתפריט'}</th>
              </tr>
            </thead>
            <tbody>
              {section.products.map((product) => (
                <tr key={product.id} className={product.isActive ? undefined : classes.mutedRow} data-testid={`monitoring-wolt-menu-row-${product.id}`}>
                  <td data-label={'מוצר'}>{product.name}</td>
                  <td data-label={'מחיר'}>{`${product.price.toFixed(1)} ₪`}</td>
                  <td data-label={'מזהה בוולט'}>{product.woltItemId ?? '-'}</td>
                  <td data-label={'כלול בתפריט'}>
                    <Switch
                      checked={Boolean(product.woltMenuIncluded)}
                      disabled={section.togglingIds.includes(product.id)}
                      onChange={() => {
                        void section.toggleIncluded(product);
                      }}
                      data-testid={`monitoring-wolt-menu-include-${product.id}`}
                    />
                  </td>
                </tr>
              ))}
              {section.products.length === 0 && (
                <tr>
                  <td colSpan={4} className={classes.empty}>{'אין מוצרים לעסק זה.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
